import React, { useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useDocument } from 'react-firebase-hooks/firestore';
import { useUserWithRole } from 'hooks/custom-auth';
import {
  Badge,
  Popconfirm,
  notification,
  Button,
  Avatar,
  Divider,
  Typography,
  List,
} from 'antd';

import {
  MailOutlined,
  ArrowLeftOutlined,
  DownloadOutlined,
  PhoneOutlined,
} from '@ant-design/icons';

import { ROUTE_EMPLOYER_PREFIX } from 'utils/routes';
import { getLabel } from 'utils/labels';
import { timeOptions } from 'utils/dates';
import { roles, sectors, GetTokensByRole } from 'utils/roles-sectors';
import MandatoryCourses from 'utils/mandatory-courses';
import Qualifications from 'utils/qualifications';
import Client from 'containers/app/client';
import { LayoutPage } from 'components/layouts';
import LoadingFullPage from 'components/loading-full-page';
import E404 from 'components/e-404';
import moment from 'moment-timezone';
import RequestBookingModal from './request-booking-modal';
import './style.css';
import RatingAverage from 'components/rating-average';

const getButtonText = (isConnecting, tokens, tokensCost) => {
  if (isConnecting) return 'Connecting ...';
  if (tokensCost > tokens)
    return `You need at least ${tokensCost} ${
      tokensCost > 1 ? 'tokens' : 'token'
    }`;
  return `Connect for ${tokensCost} ${tokensCost > 1 ? 'tokens' : 'token'}`;
};

const WorkerDetail = () => {
  const { id } = useParams();
  const history = useHistory();
  const profileRef = Client.dbProfile();
  const [user, loadingUser, errorUser] = useUserWithRole(Client.auth);
  const [profile, loadingProfile, errorProfile] = useDocument(profileRef);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);

  const bookingRequestsRef = Client.db
    .collection('booking_request')
    .where('worker', '==', id)
    .where('userId', '==', Client.currentUserId());

  const connectionRef = isConnecting
    ? null
    : Client.db
        .collection('worker_connection')
        .where('worker', '==', id)
        .where('userId', '==', Client.currentUserId());

  const ref = Client.db.doc(`users/${id}`);
  const [doc, loading, error] = useDocument(ref);
  const [
    bookingRequests,
    bookingRequestsLoading,
    bookingRequestsError,
  ] = useDocument(bookingRequestsRef);
  const [connection, connectionLoading, connectionError] = useDocument(
    connectionRef
  );

  if (
    loading ||
    loadingUser ||
    connectionLoading ||
    loadingProfile ||
    bookingRequestsLoading
  )
    return <LoadingFullPage />;

  if (error || errorProfile) throw error;
  if (errorUser) throw errorUser;
  if (connectionError) throw connectionError;
  if (bookingRequestsError) throw bookingRequestsError;

  if (!doc.data()) {
    return <E404 />;
  }

  const { tokens = 0 } = profile.data();
  const {
    info,
    experience = { text: null },
    mandatoryCourses = {},
    extraCourses = {},
    qualifications = {},
    jobRoles = {},
    referees = {},
    references = {},
    isFakeUser = false,
    certificateDBS = {},
    shiftsInfo = {},
    allowedConnections = {},
    isCovidVaccinated = {},
    covid = {},
  } = doc.data();

  // check the employer is in the allow connections list linked to the users data
  let allowedConnection = false;
  if (
    user.claims.admin === true ||
    (allowedConnections[Client.currentUserId()] != undefined &&
      allowedConnections[Client.currentUserId()])
  ) {
    allowedConnection = true;
  }

  console.log('data', doc.data());
  console.log('allowedConnection', allowedConnection);

  const { email } = info;

  // const isAlreadyRequested =
  //   bookings.docs.filter((item) => {
  //     const { status } = item.data();
  //     return status === 'pending';
  //   }).length > 0;

  const handleSubmit = async (values, actions) => {
    if (isFakeUser) return;

    actions.setSubmitting(true);
    try {
      // use the shift time startTime and endTime to create the shift request
      const { dateRange, shiftTime, ...rest } = values;
      const shift = timeOptions[shiftTime];
      console.log('shift', shiftTime, timeOptions[shiftTime]);
      const dateRangeISO = dateRange.map((d, idx) => {
        if (d) {
          const time = idx === 0 ? shift.startTime : shift.endTime;
          // eslint-disable-next-line no-underscore-dangle
          const date = moment.tz(
            `${d._d.toDateString()} ${time}`,
            'Europe/London'
          );
          return date.toISOString();
        }
        return null;
      });

      const [dateStart, dateEnd] = dateRangeISO;

      console.log('dateStart', dateStart);
      console.log('dateEnd', dateEnd);

      await Client.bookWorker({ worker: id, dateStart, dateEnd, ...rest });
      notification.success({
        message: 'Request sent!',
      });
      setIsModalOpen(false);
    } catch (err) {
      console.error('Error requesting booking', err);
      actions.setSubmitting(false);
      throw err;
    }
  };

  const handleConnect = async () => {
    try {
      setIsConnecting(true);
      await Client.connectWithWorker({ worker: id });
      setIsConnecting(false);
    } catch (err) {
      setIsConnecting(false);
      console.error(err);
    }
  };

  const connected =
    (connection && connection.docs && connection.docs.length > 0) ||
    user.claims.admin === true;

  const fullName = connected
    ? `${info.firstName} ${info.lastName}`
    : 'Connect with worker to see full details';

  const jobRolesValues = Object.values(jobRoles);
  const refereesValues = Object.values(referees);
  const referencesValues = Object.values(references);
  const tokensCost = GetTokensByRole(jobRolesValues);

  // take all the crazy shifts data and make it look readable to a human
  const makeReadableShifts = (shifts) => {
    const arr = [];
    const days = [
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
      'Sunday',
    ];

    // loop over the data
    Object.values(shifts).map((value) => {
      const data = arr[value.day] ?? {};
      data.day = days[value.day];
      data.shift =
        data.shift === undefined
          ? `${value.startTime} - ${value.endTime}`
          : `${data.shift} • ${value.startTime} - ${value.endTime}`;

      arr[value.day] = data;
      return null;
    });

    return arr;
  };

  return (
    <LayoutPage className="worker-detail-page">
      <LayoutPage.Container className="wrapper">
        <RequestBookingModal
          visible={isModalOpen}
          onSubmit={handleSubmit}
          onCancel={() => {
            setIsModalOpen(false);
          }}
        />
        <header className="worker-detail-header">
          <Button
            className="back-button"
            icon={<ArrowLeftOutlined />}
            shape="round"
            size="large"
            disabled={isModalOpen}
            onClick={() =>
              history.push(`${ROUTE_EMPLOYER_PREFIX}/worker-search`)
            }
          >
            Back
          </Button>

          <Badge count={bookingRequests.docs.length}>
            <Avatar
              src={info.photoURL}
              size={150}
              style={!connected && { filter: 'blur(5px)' }}
            />
          </Badge>
          <RatingAverage role="isWorker" />
          <h1>{fullName}</h1>

          <div className="worker-detail-actions">
            {!connected ? (
              <Popconfirm
                title="Are you sure?"
                onConfirm={handleConnect}
                onCancel={() => {}}
                okText="Yes"
                cancelText="No"
                disabled={isConnecting || tokensCost > tokens}
              >
                <Button
                  type="primary"
                  disabled={isConnecting || tokensCost > tokens}
                  loading={isConnecting}
                >
                  {getButtonText(isConnecting, tokens, tokensCost)}
                </Button>
              </Popconfirm>
            ) : null}

            {connected ? (
              <Button
                type="primary"
                onClick={() => {
                  setIsModalOpen(true);
                }}
                disabled={isModalOpen}
              >
                Send a booking request
              </Button>
            ) : null}

            {connected && allowedConnection ? (
              <>
                <Button
                  href={`tel:${info.mobilePhone}`}
                  icon={<PhoneOutlined />}
                >
                  {info.mobilePhone}
                </Button>
                <Button icon={<MailOutlined />} href={`mailto:${email}`}>
                  {email}
                </Button>
              </>
            ) : null}
          </div>
        </header>

        <section className="worker-detail-info">
          <Divider />
          <header className="worker-detail-info-header">
            <h2>Covid Vaccination</h2>
          </header>
          {isCovidVaccinated.vaccinated ? (
            <p>
              <strong>{covid.vaccineType}</strong>
              <br />
              1st vaccination date :{' '}
              {covid.vaccineDateFirst !== ''
                ? moment(covid.vaccineDateFirst).format('DD-MM-YYYY')
                : 'Not set'}
              <br />
              2nd vaccination date :{' '}
              {covid.vaccineDateSecond !== ''
                ? moment(covid.vaccineDateSecond).format('DD-MM-YYYY')
                : 'Not set'}
            </p>
          ) : (
            <p>Not vaccinated</p>
          )}
        </section>

        <section className="worker-detail-info">
          <Divider />

          <h2>Completed courses</h2>

          <List itemLayout="horizontal">
            {Object.entries(mandatoryCourses).map(([key, value]) => {
              return (
                <List.Item
                  key={key}
                  actions={
                    connected &&
                    allowedConnection &&
                    value &&
                    value.fileURL != null
                      ? [
                          <Button
                            target="_blank"
                            type="primary"
                            icon={<DownloadOutlined />}
                            size="small"
                            href={value.fileURL}
                          >
                            Download
                          </Button>,
                        ]
                      : null
                  }
                >
                  {getLabel(key, MandatoryCourses)}
                </List.Item>
              );
            })}

            {Object.entries(extraCourses).map(([key, value]) => {
              return (
                <List.Item
                  key={key}
                  actions={
                    connected &&
                    allowedConnection &&
                    value &&
                    value.fileURL != null
                      ? [
                          <Button
                            target="_blank"
                            type="primary"
                            icon={<DownloadOutlined />}
                            size="small"
                            href={value.fileURL}
                          >
                            Download
                          </Button>,
                        ]
                      : null
                  }
                >
                  {value.name}
                </List.Item>
              );
            })}
          </List>

          <Divider />

          <h2>Qualifications</h2>
          {Object.keys(qualifications).length > 0 ? (
            <List itemLayout="horizontal">
              {Object.entries(qualifications)
                .filter(([key, value]) => {
                  return value != null;
                })
                .map(([key, value]) => {
                  return (
                    <List.Item
                      key={key}
                      actions={
                        connected && allowedConnection
                          ? [
                              <Button
                                target="_blank"
                                type="primary"
                                icon={<DownloadOutlined />}
                                size="small"
                                href={value.fileURL}
                              >
                                Download
                              </Button>,
                            ]
                          : null
                      }
                    >
                      {getLabel(key, Qualifications)}
                    </List.Item>
                  );
                })}
            </List>
          ) : (
            <Typography>
              <p>No qualifications available</p>
            </Typography>
          )}

          <Divider />

          <h2>Job Roles</h2>
          {jobRolesValues.length > 0 ? (
            <List itemLayout="horizontal">
              {jobRolesValues.map((item, i) => {
                return (
                  <List.Item key={i}>
                    {getLabel(item.role, roles)}
                    {' - '}
                    {getLabel(item.sector, sectors)}
                  </List.Item>
                );
              })}
            </List>
          ) : (
            <Typography>
              <p>No job roles available</p>
            </Typography>
          )}
        </section>

        {connected && allowedConnection ? (
          <>
            <section className="worker-detail-info">
              <Divider />

              <h2>Referees</h2>
              {refereesValues.length > 0 ? (
                <List itemLayout="horizontal">
                  {refereesValues.map((item, i) => {
                    return (
                      <List.Item key={i}>
                        <p>
                          <strong>{item.name}</strong>
                          <br />
                          {item.role}
                          <br />
                          {item.organisation}
                          <br />
                          {item.email}
                          <br />
                          {item.telephone}
                        </p>
                      </List.Item>
                    );
                  })}
                </List>
              ) : (
                <Typography>
                  <p>No referees available</p>
                </Typography>
              )}

              <Divider />

              <h2>References</h2>
              {referencesValues.length > 0 ? (
                <List itemLayout="horizontal">
                  {referencesValues.map((item, i) => {
                    return (
                      <List.Item
                        key={i}
                        actions={
                          connected &&
                          allowedConnection &&
                          item &&
                          item.fileURL != null
                            ? [
                                <Button
                                  target="_blank"
                                  type="primary"
                                  icon={<DownloadOutlined />}
                                  size="small"
                                  href={item.fileURL}
                                >
                                  Download
                                </Button>,
                              ]
                            : null
                        }
                      >
                        Reference {i + 1}
                      </List.Item>
                    );
                  })}
                </List>
              ) : (
                <Typography>
                  <p>No references available</p>
                </Typography>
              )}

              <Divider />

              <header className="worker-detail-info-header">
                <h2>DBS id: {certificateDBS.id}</h2>
                <Button
                  target="_blank"
                  type="primary"
                  icon={<DownloadOutlined />}
                  size="large"
                  href={certificateDBS.fileURL}
                >
                  Download Certificate
                </Button>
              </header>
            </section>

            <section className="worker-detail-info">
              <Divider />
              <header className="worker-detail-info-header">
                <h2>Experience</h2>
                <Button
                  target="_blank"
                  type="primary"
                  icon={<DownloadOutlined />}
                  size="large"
                  href={experience.fileURL}
                >
                  Download CV
                </Button>
              </header>
              {experience.text ? (
                <Typography>
                  <p>{experience.text}</p>
                </Typography>
              ) : null}
            </section>
          </>
        ) : null}

        <section className="worker-detail-info">
          <Divider />
          <h2>Available Shift Times</h2>
          {shiftsInfo.dateTimeAvailable &&
          Object.keys(shiftsInfo.dateTimeAvailable).length > 0 ? (
            <List itemLayout="horizontal">
              {makeReadableShifts(shiftsInfo.dateTimeAvailable).map(
                (item, i) => {
                  return (
                    <List.Item key={i}>
                      <strong>{item.day}</strong> : {item.shift}
                    </List.Item>
                  );
                }
              )}
            </List>
          ) : (
            <Typography>
              <p>No shifts available</p>
            </Typography>
          )}
        </section>
      </LayoutPage.Container>
    </LayoutPage>
  );
};

export default WorkerDetail;
